import type { TFunction } from 'i18next';
import { stationCalendarDay } from '../../utils/stationTime';
import { formatDay } from '../../pages/organizations/allocationWindows/windowDraft';
import { ORGANIZATION_TYPE_LABEL_KEY } from '../../pages/organizations/organizationTypeLabels';
import type { CustomerOrganizationType } from '../../api/organization/organizationApiTypes';
import type {
    OnboardingImportAction,
    OnboardingImportChangeValue,
    OnboardingImportIssue,
    OnboardingImportItem,
    OnboardingImportPriceChange,
    OnboardingImportPriceRow,
    OnboardingImportReport,
    OnboardingImportSection,
    OnboardingImportWindowChange,
} from '../../api/account/accountApiTypes';

/** The picked file ends in ".txt": the page says that is fine — the content decides. */
export const isTextFileName = (name: string): boolean => /\.txt$/i.test(name.trim());

/**
 * How the preview groups the items, in this order (Konto, Produkte, Räume,
 * Preise, Buchungsregeln, Kundenklassen, Organisationen and their parts,
 * Dokumente, then the file's own unreadable "manuell anlegen" entries and
 * unknown sections).
 */
export const IMPORT_GROUPS: readonly OnboardingImportSection[] = [
    'account', 'product', 'room', 'price', 'setting', 'customerClass', 'organization',
    'unit', 'contact', 'contract', 'access', 'window', 'document', 'manual', 'file',
];

/** One group of the preview: its heading (`onboarding_import.group.<group>`) and the sections it shows. */
export interface ImportGroup {
    group: string;
    sections: readonly OnboardingImportSection[];
}

const ONE_GROUP_PER_SECTION: readonly ImportGroup[] = IMPORT_GROUPS.map((section) => ({ group: section, sections: [section] }));

/** The items per group, in the layout's order (default: one group per section); empty groups are dropped. */
export const groupItems = (items: OnboardingImportItem[], layout: readonly ImportGroup[] = ONE_GROUP_PER_SECTION) =>
    layout
        .map(({ group, sections }) => ({ group, items: items.filter((item) => sections.includes(item.section)) }))
        .filter((g) => g.items.length > 0);

/** What happens per action within a group; a skipped item is counted apart (`countSkipped`), never as created or changed. */
export const countByAction = (items: OnboardingImportItem[], group: OnboardingImportSection): Record<OnboardingImportAction, number> => {
    const counts: Record<OnboardingImportAction, number> = { create: 0, update: 0, unchanged: 0 };
    for (const item of items) if (item.section === group && !item.skipped) counts[item.action] += 1;
    return counts;
};

export const countSkipped = (items: OnboardingImportItem[], group?: OnboardingImportSection): number =>
    items.filter((item) => item.skipped && (group === undefined || item.section === group)).length;

/** "Neu", "Wird geändert", "Unverändert" — or, for a skipped item, "Wird nicht angelegt" / "Wird nicht übernommen". */
export const actionKey = (item: OnboardingImportItem): string => {
    if (!item.skipped) return `onboarding_import.action.${item.action}`;
    return item.action === 'create' ? 'onboarding_import.action.skipped_create' : 'onboarding_import.action.skipped_update';
};

/** Shape codes `@kolula/shared` sends (`OnboardingShapeCode`); an unknown one shows the server's message. */
const SHAPE_CODES = new Set([
    'not_object', 'unknown_field', 'required', 'empty', 'not_string', 'one_of', 'format',
    'not_number', 'not_integer', 'min', 'max', 'not_list', 'not_boolean', 'duplicate_name', 'version',
    'stay_only', 'set_on_product', 'product_name_taken',
]);

/** Field labels the preview's changes use; any other field shows its name. */
const FIELD_LABELS = new Set([
    'default_cost_center', 'leitweg_id', 'show_on_public_plan', 'agreement_number', 'start_date', 'expiry_date',
    'weekday', 'startTime', 'endTime', 'validFrom', 'validTo', 'expiryDate', 'startDate', 'email', 'windows',
    'name', 'country', 'type', 'street', 'postalCode', 'city', 'managingDirector', 'hrb', 'hrbCourt', 'supervisoryBoardChair',
    'vatId', 'taxNumber', 'accountHolder', 'iban', 'bic', 'bankName', 'email', 'phone', 'website', 'legal_name', 'display_name',
    'customer_class_id', 'billing_street', 'billing_postal_code', 'billing_city', 'billing_country', 'vat_id', 'billing_email',
    'invoice_contact', 'notes', 'description', 'descriptionEn', 'capacity', 'floor', 'openingHours',
    'houseNumber', 'address', 'useCase', 'product', 'legalName', 'price', 'days', 'durationInMinutes', 'title', 'topic', 'note',
    'beds', 'baths', 'kitchen', 'rooms', 'amenities', 'facilityDescription', 'facilityDescriptionEn', 'importantNote',
    'importantNoteEn', 'policies', 'policiesEn', 'longStayDiscounts', 'minNights', 'extra', 'extraNameEn',
]);

const shapeCodeText = (code: string, params: Record<string, string | number>, t: TFunction): string => {
    const format = params.format === 'time' ? 'time' : 'country';
    return t(code === 'format' ? `onboarding_import.shape.format_${format}` : `onboarding_import.shape.${code}`, params);
};

const ACCOUNT_KEYS = new Set(['organization', 'legalNotice', 'bankDetails', 'contact']);

/** A row's React key: a duplicate shares its name with the kept entry, so its entry number tells them apart. */
export const itemRowKey = (item: OnboardingImportItem): string => `${item.section}-${item.key}-${item.detail?.duplicateIndex ?? ''}`;

/** A part of an organisation, named with what it belongs to: "Turnen (TSV e. V.)", "Halle A — Hallenvertrag, TSV e. V.". */
const partLabel = (item: OnboardingImportItem, name: string, t: TFunction): string | null => {
    const { parent } = item;
    if (!parent) return null;
    if (item.section === 'access' && parent.contract) {
        return t('onboarding_import.part.room', { name, contract: parent.contract, organization: parent.organization });
    }
    if (item.section === 'window' && parent.contract) {
        return t('onboarding_import.part.windows', { name, contract: parent.contract, organization: parent.organization });
    }
    return t('onboarding_import.part.of', { name, organization: parent.organization });
};

/** The item's own name as a person reads it: a duplicate's entry number, a nameless entry's number, an account section's title. */
const ownName = (item: OnboardingImportItem, t: TFunction): string => {
    if (item.section === 'account' && ACCOUNT_KEYS.has(item.key)) return t(`onboarding_import.account.${item.key}`);
    if (item.section === 'setting' && item.key === 'apartmentBooking') return t('onboarding_import.setting.apartmentBooking');
    // The second of a duplicate pair: its entry in the file, never a JSON path.
    const { duplicateIndex } = item.detail ?? {};
    if (typeof duplicateIndex === 'number') return t('onboarding_import.duplicate_entry', { name: item.key, index: duplicateIndex });
    // An entry without a name is keyed by its place in the file ("organizations[0]"): its number, never the path.
    const unnamed = /^[a-zA-Z]+\[(\d+)\]$/.exec(item.key);
    return unnamed ? t('onboarding_import.unnamed_entry', { index: Number(unnamed[1]) + 1 }) : item.key;
};

/** A top-level section of the file: one the other import reads, or one kolula does not know. */
const sectionLabel = (item: OnboardingImportItem, name: string, t: TFunction): string =>
    (item.issues.some((i) => i.code === 'not_organization_section')
        ? t('onboarding_import.other_section', { name })
        : t('onboarding_import.unknown_section', { name }));

/** The row label: the account section's name, or the item's own name, with what the server adds. */
export const itemLabel = (item: OnboardingImportItem, t: TFunction): string => {
    const name = ownName(item, t);
    if (item.section === 'file') return sectionLabel(item, name, t);
    const part = partLabel(item, name, t);
    if (part) return part;
    const { house, rows, product, rooms } = item.detail ?? {};
    if (item.section === 'product' && typeof rooms === 'number') return t('onboarding_import.product_rooms', { name, count: rooms });
    if (item.section === 'price' && typeof rows === 'number') return t('onboarding_import.price_rows', { name, count: rows });
    if (item.section !== 'room') return name;
    const inProduct = typeof product === 'string' && product ? t('onboarding_import.in_product', { name, product }) : name;
    return typeof house === 'string' && house ? t('onboarding_import.in_house', { name: inProduct, house }) : inProduct;
};

/**
 * Issue codes with their own text (`onboarding_import.issue.<code>`, filled
 * from the issue's `params`). The rule codes (`invalid`, `invalid_email` …)
 * are the server's `ruleIssueCode`. Any other code reads as `invalid` with its
 * field — in German never the server's English message; the English screen
 * shows that message as the detail.
 */
const ISSUE_CODES = new Set([
    'no_prices', 'house_hours', 'existing_house', 'address_exists', 'house_hours_kept', 'use_case_kept', 'address_kept',
    'photos_existing_room', 'photos_unknown_room', 'capacity_in_use', 'shared_price_list', 'free_use_case', 'no_price_list',
    'unknown_class', 'ambiguous', 'terms_without_title', 'terms_pdf_missing', 'no_slug', 'register_not_company',
    'use_case_not_enabled', 'object_limit', 'empty_prices_existing', 'zero_rows_kept', 'reserved_class', 'terms_title',
    'invalid', 'invalid_email', 'invalid_country', 'hours_order', 'hours_duplicate', 'hours_range', 'price_time_order',
    'price_window_days', 'price_duplicate_scope', 'price_days', 'invalid_use_case', 'invalid_address',
    'unknown_product', 'product_use_case', 'product_kept', 'product_without_rooms', 'shared_discounts',
    'product_name_taken', 'product_is_room', 'use_case_mismatch', 'discounts_no_rooms',
    'min_stay_not_configured', 'min_stay_all_apartments', 'min_stay_no_use_case', 'min_stay_category_override',
    'rooms_skipped', 'photos_skipped_room', 'depends_on_room', 'depends_on_product', 'depends_on_customerClass',
    // The organisation import (new-backend/admin/src/lib/onboarding/organizationParts.ts) and the
    // onboarding import's pointer to it.
    'invalid_value', 'contract_expired', 'contract_price_in_note', 'owner_required', 'unknown_room', 'ambiguous_room',
    'room_twice', 'no_running_contract', 'room_dedicated', 'room_access_refused', 'windows_incomplete', 'window_ended',
    'window_list_all_ended', 'grant_dedicated', 'window_invalid', 'window_overlap_own', 'window_overlap',
    'window_has_releases', 'window_bookings', 'window_validity_missing', 'organization_import_only',
    'not_organization_section',
]);

/** What an item depends on, when it is skipped with it (`depends_on`, `params.section`). */
const DEPENDS_ON = new Set(['room', 'product', 'customerClass', 'organization', 'contract', 'access']);

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A calendar day in a param reads as the screen writes days ("31.07.2027"); a list of days, each. */
const readableParams = (params: Record<string, string | number>, language: string): Record<string, string | number> => {
    const lang = language.toLowerCase().startsWith('en') ? 'en' : 'de';
    return Object.fromEntries(Object.entries(params).map(([key, value]) => {
        if (typeof value !== 'string') return [key, value];
        const parts = value.split(', ');
        return [key, parts.every((part) => DAY.test(part)) ? parts.map((day) => formatDay(day, lang)).join(', ') : value];
    }));
};

/**
 * A field inside an item as a person reads it: "address.street" → "Straße",
 * "prices[0].price" → "Preis (Zeile 1)". A field without a label shows its name.
 */
export const fieldPathLabel = (path: string, t: TFunction): string => {
    const parts = path.split(/[.[\]]/).filter(Boolean);
    const name = [...parts].reverse().find((p) => !/^\d+$/.test(p)) ?? path;
    const index = parts.find((p) => /^\d+$/.test(p));
    const label = FIELD_LABELS.has(name) ? t(`onboarding_import.field.${name}`, { name: '' }) : name;
    return index === undefined ? label : t('onboarding_import.field_row', { field: label, row: Number(index) + 1 });
};

/**
 * The object limit's own variants (`object_limit` is the trial's text for one
 * kind): a contract's limit asks to contact kolula (ADR-0001 Amendment 6), and
 * a trial that has rooms and stations names both — they are counted together
 * (PO, 2026-11-02). Null for any other issue.
 */
const objectLimitIssueKey = (issue: OnboardingImportIssue): string | null => {
    if (issue.code !== 'object_limit') return null;
    if (issue.params?.basis === 'contract') return 'onboarding_import.issue.object_limit_contract';
    if (issue.params?.unit === 'combined') return 'onboarding_import.issue.object_limit_combined';
    return null;
};

/** An issue's text without the "Fehler"/"Hinweis" prefix. */
export const issueBody = (issue: OnboardingImportIssue, t: TFunction, language = 'de'): string => {
    const params = readableParams({ field: issue.field ?? '', ...(issue.params ?? {}) }, language);
    const section = String(issue.params?.section ?? '');
    if (issue.code === 'depends_on' && DEPENDS_ON.has(section)) return t(`onboarding_import.issue.depends_on_${section}`, params);
    const limitKey = objectLimitIssueKey(issue);
    if (limitKey) return t(limitKey, params);
    // A contact's address is the item itself: the text names the address, not the field.
    if (issue.code === 'invalid_email' && issue.field === 'email') return t('onboarding_import.issue.contact_invalid_email', params);
    if (issue.code === 'window_invalid') {
        return t('onboarding_import.issue.window_invalid', { ...params, field: fieldPathLabel(String(issue.params?.field ?? ''), t) });
    }
    if (issue.code === 'window_overlap_own' && issue.params?.ended === 1) return t('onboarding_import.issue.window_overlap_own_ended', params);
    if (ISSUE_CODES.has(issue.code)) return t(`onboarding_import.issue.${issue.code}`, params);
    // A shape problem inside the item: the field in words, then what is wrong with it.
    if (SHAPE_CODES.has(issue.code) && issue.code !== 'version') {
        const text = shapeCodeText(issue.code, issue.params ?? {}, t);
        return issue.field ? `${fieldPathLabel(issue.field, t)}: ${text}` : text;
    }
    if (language.toLowerCase().startsWith('en')) return t('onboarding_import.issue.rule', { detail: issue.message });
    return t('onboarding_import.issue.invalid', params);
};

export const issueText = (issue: OnboardingImportIssue, t: TFunction, language = 'de'): string => {
    const prefix = t(issue.level === 'error' ? 'onboarding_import.level.error' : 'onboarding_import.level.warning');
    return `${prefix}: ${issueBody(issue, t, language)}`;
};

/** The fields a skipped item lacks ("required" / "empty"), in words — "Straße, PLZ"; empty when it is skipped for another reason. */
export const missingFields = (item: OnboardingImportItem, t: TFunction): string[] =>
    item.issues
        .filter((issue) => issue.level === 'error' && (issue.code === 'required' || issue.code === 'empty') && issue.field)
        .map((issue) => fieldPathLabel(issue.field as string, t));

type ChangeEntry = NonNullable<OnboardingImportItem['changes']>[number];

/**
 * A value as the line shows it. Amenities travel as vocabulary keys and read
 * in the viewer's language (`amenities.<key>`; a freeform entry as stored);
 * an extra travels as "<price>" plus " *" when it is booked with every booking.
 */
const changeValue = (value: OnboardingImportChangeValue, field: string, t: TFunction, language: string): string => {
    if (value === null || value === '') return t('common.missing');
    // A contract's day travels as the stored instant; it reads as the station's calendar day.
    if ((field === 'start_date' || field === 'expiry_date') && !Number.isNaN(Date.parse(value))) {
        return formatDay(stationCalendarDay(value), language.toLowerCase().startsWith('en') ? 'en' : 'de');
    }
    if (field === 'show_on_public_plan' && (value === 'true' || value === 'false')) return t(`onboarding_import.value.${value}`);
    if (field === 'amenities') {
        return value.split(', ').map((key) => t(`amenities.${key}`, { defaultValue: key })).join(', ');
    }
    if (field === 'extra') {
        const mandatory = value.endsWith(' *');
        const price = euro(Number(mandatory ? value.slice(0, -2) : value), language);
        return mandatory ? t('onboarding_import.extra_mandatory', { price }) : price;
    }
    return value;
};

const fieldLabel = (change: ChangeEntry, t: TFunction): string => {
    if (!FIELD_LABELS.has(change.field)) return change.field;
    return t(`onboarding_import.field.${change.field}`, { name: change.label ?? '' });
};

/** One line per changed field: "Telefon: +49 1 2345 → +49 1 9999". */
export const changeLines = (item: OnboardingImportItem, t: TFunction, language = 'de'): string[] => {
    // An organisation's type travels as its code ("club"); it reads as the organisation screen names it.
    const value = (v: OnboardingImportChangeValue, field: string): string => (
        item.section === 'organization' && field === 'type' && v !== null && v in ORGANIZATION_TYPE_LABEL_KEY
            ? t(ORGANIZATION_TYPE_LABEL_KEY[v as CustomerOrganizationType])
            : changeValue(v, field, t, language)
    );
    return (item.changes ?? []).map((change) => t('onboarding_import.change', {
        field: fieldLabel(change, t),
        from: value(change.from, change.field),
        to: value(change.to, change.field),
    }));
};

const euro = (value: number, language: string): string =>
    value.toLocaleString(language.toLowerCase().startsWith('en') ? 'en-GB' : 'de-DE', { style: 'currency', currency: 'EUR' });

/** A price row in words: "Mo–Fr · ganzer Tag · Ortsvereine". */
export const priceRowText = (row: OnboardingImportPriceRow, t: TFunction): string => {
    const named = row.days === 'weekday' || row.days === 'weekend';
    const days = named ? t(`onboarding_import.price.${row.days}`) : (row.days ?? t('onboarding_import.price.any_day'));
    const length = row.isFullDay
        ? t('onboarding_import.price.full_day')
        : t('onboarding_import.price.minutes', { count: row.durationInMinutes ?? 0 });
    const window = row.startTime && row.endTime ? `${row.startTime}–${row.endTime}` : null;
    const who = row.customerClass ?? t('onboarding_import.price.general');
    return [days, length, window, who].filter(Boolean).join(' · ');
};

/** One line per price row an update creates, changes or removes. */
export const priceChangeLines = (
    item: OnboardingImportItem,
    t: TFunction,
    language = 'de',
): Array<{ kind: OnboardingImportPriceChange['kind'], text: string }> =>
    (item.priceChanges ?? []).map((change) => ({
        kind: change.kind,
        text: t(`onboarding_import.price_change.${change.kind}`, {
            row: priceRowText(change.row, t),
            price: euro(change.row.price, language),
            from: change.before ? euro(change.before.price, language) : '',
        }),
    }));

/** "Di 18:00–20:00, 01.09.2030–30.06.2031" — one fixed weekly time as the window editor writes it. */
export const windowText = (w: OnboardingImportWindowChange, t: TFunction, language = 'de'): string => {
    const lang = language.toLowerCase().startsWith('en') ? 'en' : 'de';
    const weekday = t(`onboarding_import.weekday.${w.weekday}`);
    return t('onboarding_import.window', {
        weekday, start: w.startTime, end: w.endTime, from: formatDay(w.validFrom, lang), to: formatDay(w.validTo, lang),
    });
};

/** One line per fixed weekly time the file creates, keeps or removes ("Wird entfernt"), with its released dates. */
export const windowChangeLines = (
    item: OnboardingImportItem,
    t: TFunction,
    language = 'de',
): Array<{ kind: OnboardingImportWindowChange['kind'], text: string }> =>
    (item.windowChanges ?? []).map((w) => {
        const released = w.releaseDates?.length
            ? ` ${t('onboarding_import.window_released', { count: w.releaseDates.length })}`
            : '';
        const change = t(`onboarding_import.window_change.${w.kind}`, { window: windowText(w, t, language) });
        return { kind: w.kind, text: `${change}${released}` };
    });

/**
 * What a contact person row means (`detail.outcome`, the contact writer's
 * decision), and what a room row is now (`detail.accessMode`): one line each
 * under the name, or null.
 */
export const partDetailLine = (item: OnboardingImportItem, t: TFunction): string | null => {
    const { outcome, accessMode } = item.detail ?? {};
    if (item.section === 'contact' && typeof outcome === 'string' && !item.skipped) return t(`onboarding_import.contact_outcome.${outcome}`);
    if (item.section === 'access' && typeof accessMode === 'string' && !item.skipped) {
        return t('onboarding_import.access_now', { mode: t(`organization.rooms.mode.${accessMode}`) });
    }
    return null;
};

/** What the import itself knows is left to do by hand (codes with params), in a sentence. */
export const noticeText = (notice: { code: string, params?: Record<string, string | number> }, t: TFunction): string =>
    t(`onboarding_import.notice.${notice.code}`, notice.params ?? {});

/** A refusal the page names in its own words; null means "show the server's message". */
const ERROR_CODES: Record<string, string> = {
    file_missing: 'onboarding_import.error.unreadable',
    file_unreadable: 'onboarding_import.error.not_json',
    file_too_large: 'onboarding_import.error.file_too_large',
    attachments_not_here: 'onboarding_import.error.attachments_not_here',
    owner_required: 'onboarding_import.error.owner_required',
    master_required: 'onboarding_import.error.owner_required',
    onboarding_import_unsupported: 'onboarding_import.error.unsupported',
    not_onboarding_file: 'onboarding_import.error.not_format',
};

/** No usable answer: none at all, or a gateway giving up on the server. The apply may have committed. */
export const isUnanswered = (err: unknown): boolean => {
    const status = (err as { status?: number } | undefined)?.status;
    return status === undefined || status === 502 || status === 503 || status === 504;
};

export const errorKeyOf = (err: unknown): string | null => {
    const e = err as { status?: number, data?: { code?: unknown } } | undefined;
    if (e && e.status === undefined) return 'onboarding_import.error.network';
    const code = typeof e?.data?.code === 'string' ? e.data.code : '';
    return ERROR_CODES[code] ?? null;
};

/** What a tenant gives the AI: the prompt, then the JSON Schema — one text, so the two cannot drift apart. */
export const promptWithSchema = (format: { prompt: string, schema: unknown }): string =>
    `${format.prompt}\n\nJSON-Schema:\n${JSON.stringify(format.schema, null, 2)}\n`;

export const downloadText = (text: string, fileName: string, type: string): void => {
    const url = URL.createObjectURL(new Blob([text], { type: `${type};charset=utf-8` }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

/** Why a file is not an onboarding file at all, one line per problem (the top level, or `version`). */
export const shapeProblemText = (
    problem: OnboardingImportReport['shapeProblems'][number],
    t: TFunction,
): string => {
    if (problem.path === '$' && problem.code === 'not_object') return t('onboarding_import.shape.not_file_object');
    if (problem.path === '$.version') return t('onboarding_import.shape.not_file_version');
    if (!SHAPE_CODES.has(problem.code)) return `${problem.path}: ${problem.message}`;
    return `${problem.path}: ${shapeCodeText(problem.code, problem.params ?? {}, t)}`;
};
